import React, {useCallback} from 'react';

import {NotificationContent} from 'src/modules/internal-notification/components/notification-content';

import Button from '@pb/kit-component-button';
import {DialogFooter, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {Group} from '@pb/kit-component-group';
import Loading from '@pb/kit-component-loading';

import {useNotificationContentReader} from './useNotificationContentReader';

type NotificationDialogProps = {
    notifications: {title: string, notificationId: string}[],
    onClose: () => void,
    onRead?: (id: string) => void,
};

export const NOTIFICATION_MODAL_WIDTH = 912;

export const NotificationModal = ({notifications, onClose, onRead}: NotificationDialogProps) => {
    const {
        isLoading,
        loadingError,
        data,
    } = useNotificationContentReader({notifications});

    const [currentIndex, setCurrentIndex] = React.useState(0);

    const toPrevBlock = useCallback(() => {
        setCurrentIndex(currentIndex - 1);
    }, [currentIndex]);

    const toNextBlock = useCallback(() => {
        setCurrentIndex(currentIndex + 1);
    }, [currentIndex]);

    if (isLoading) {
        return (
            <>
                <DialogHeader onClose={onClose}>
                    Notification
                </DialogHeader>
                <DialogMain style={{padding: 24}}>
                    <Loading fast={true}/>
                </DialogMain>
            </>
        );
    }

    if (loadingError) {
        return (
            <>
                <DialogHeader onClose={onClose}>
                    Notification
                </DialogHeader>
                <DialogMain style={{padding: 24}}>
                    {loadingError.message}
                </DialogMain>
            </>
        );
    }

    if (data.length) {
        return (
            <>
                <DialogHeader onClose={onClose}>
                    {data[currentIndex].title}
                </DialogHeader>
                <DialogMain style={{padding: 24}}>
                    <NotificationContent
                        width={864}
                        maxHeight={460}
                        id={data[currentIndex].notificationId}
                        content={data[currentIndex].content}
                        onRead={onRead}
                    />
                </DialogMain>
                <DialogFooter>
                    <Group>
                        {(notifications.length > 1 && currentIndex !== 0) ? (
                            <Button variant="Type.TERTIARY" onClick={toPrevBlock}>
                                Previous
                            </Button>
                        ) : null}
                        {(notifications.length > 1 && currentIndex !== notifications.length - 1) ? (
                            <Button variant="Type.TERTIARY" onClick={toNextBlock}>
                                Next
                            </Button>
                        ) : null}
                        {notifications.length === 1 && (
                            <Button variant="Type.SECONDARY" onClick={onClose}>
                                Close
                            </Button>
                        )}
                    </Group>
                </DialogFooter>
            </>
        );
    }

    return null;
};
